import type { RouteLocationNormalized } from 'vue-router';
import { useMenuStore, useSettingsStore, useDocumentStore } from '../..';
import { isString } from '../../../utils/is';
import type { TabProps } from './types';
import { defineStore } from 'pinia';
import { ref, computed, reactive, onMounted } from 'vue';
import { getWujieOrQiankun } from '../../../utils/getWujieOrQiankun';
import { useRouter } from 'vue-router';
import { getEmpCode } from '../../../utils/auth';
import { equalToRoute, getDecodePathname } from '../../../utils/url';
import { useFavoritesCacheStore } from '../favorites/cache';
import { ElMessage } from 'element-plus';

const formatTag = (
  route: RouteLocationNormalized,
  routeSettings: {
    baseOn?: ('frontend' | 'filesystem') | undefined;
    baseURL?: string | undefined;
    homeTitle?: string | undefined;
    homePath?: string | undefined;
    getNotFoundTabTitle?: ((route: RouteLocationNormalized) => string) | undefined;
  },
): TabProps => {
  const { name, meta, fullPath, path, query } = route;
  // 若是子应用，去菜单数据获取名称
  let subAppTitle = '';
  if (meta.isSubApp || name === 'notFound' || !meta.title || meta.title === '404') {
    const menuStore = useMenuStore();
    const favoritesCache = useFavoritesCacheStore()
    const decodePath = getDecodePathname(route.path)

    subAppTitle = menuStore.flatMenuObj[decodePath]?.title || favoritesCache.data[decodePath]?.title || '';
    if (!subAppTitle) {
      if (routeSettings?.getNotFoundTabTitle) {
        subAppTitle = routeSettings?.getNotFoundTabTitle(route);
      } else {
        subAppTitle = (query?.title as string) || ''; // 当未查找到时（如第三方跳转页面，菜单中不存在时），使用路由中的名称
      }
    }
  }

  return {
    title: subAppTitle || (meta.title as string) || '',
    name: String(name),
    fullPath,
    path,
    query,
    ignoreCache: meta.ignoreCache as boolean,
  };
};

const useTabBarStore = defineStore(
  'tabBar',
  () => {
    const settingsStore = useSettingsStore();
    const { setDocumentTitle } = useDocumentStore()
    const router = useRouter();
    const menuStore = useMenuStore();

    const DEFAULT_HOME_TITLE = '工作台'; // 默认标题
    const homeTitle = computed(() => settingsStore.settings.route.homeTitle || DEFAULT_HOME_TITLE);
    const homeTitleFromSubApp = ref(''); // 子应用 homePath 标题
    const homePath = computed(() => settingsStore.settings.route.homePath || '/');

    // 默认固定tab
    const DEFAULT_ROUTE = computed<TabProps>(() => {
      return {
        title: homeTitleFromSubApp.value || homeTitle.value,
        name: 'workplace',
        fullPath: homePath.value,
        path: new URL(`http://localhost${homePath.value}`).pathname,
      };
    });

    const { name, meta } = router.currentRoute.value
    const defaultCacheTabList = typeof name === 'string' && !meta.ignoreCache && !['noFound', '404'].includes(name) ? [name] : []
    const cacheTabList = reactive(new Set(defaultCacheTabList));
    const tagList = ref<TabProps[]>([DEFAULT_ROUTE.value]);
    const tagObjByUpdateTab = ref<Record<string, TabProps>>({})
    // 当前会话是否已经提示过"标签数达上限"，避免反复打扰
    const hasNotifiedEvict = ref(false);
    const tagListByShow = computed(() =>
      tagList.value.map((item) => {
        const v = menuStore.flatMenuObj[item.path]; // 避免判断path完全相等，存在参数不同的情况
        const target = tagObjByUpdateTab.value[item.path]
        return {
          ...item,
          /** 用户手动更新的 tag 优先级最高 */
          title: target?.title || v?.title || item.title,
        };
      }),
    );

    /**
     * 在新增标签前按 maxCount 做 LRU 淘汰，确保新增后 tagList.length <= maxCount。
     * - 不配置 maxCount / 为 0 / Infinity：直接放行，不影响任何逻辑
     * - 已达上限：在「非首页、非固定、非当前激活」的 tab 中，按 lastVisitedAt 升序淘汰最久未访问的一个
     * - 当前会话首次淘汰会给用户一次性提示
     * - 极端情况下全部都是受保护的 tab，无可淘汰对象时仍放行（保护用户当前操作）
     */
    function enforceMaxCount() {
      const max = settingsStore.settings.tabBar?.maxCount;
      if (!max || max <= 0 || max === Infinity) return;
      // 新增前 length === max 即意味着 push 之后会超出，需要先腾出一个位置
      if (tagList.value.length < max) return;

      const currentPath = router.currentRoute.value.path;
      const candidate = tagList.value
        .map((tab, i) => ({ tab, i }))
        .filter(({ tab, i }) => {
          if (i === 0) return false; // 首页
          if (tab.isPinned) return false; // 固定标签
          if (equalToRoute(tab.path, undefined, currentPath)) return false; // 当前激活
          return true;
        })
        .sort((a, b) => (a.tab.lastVisitedAt ?? 0) - (b.tab.lastVisitedAt ?? 0))[0];

      if (!candidate) return; // 全是受保护项，放弃淘汰

      const oldTabList = tagList.value.slice();
      cacheTabList.delete(candidate.tab.name);
      tagList.value.splice(candidate.i, 1);

      if (!hasNotifiedEvict.value) {
        hasNotifiedEvict.value = true;
        ElMessage({
          type: 'warning',
          message: `标签数已达上限 ${max} 个，后续将自动关闭最久未访问的标签`,
          duration: 6000,
          showClose: true,
        });
      }

      getWujieOrQiankun().then(({ bus }) => {
        bus.$emit('afterUpdateTab', { type: 'delete', tab: candidate.tab, tabList: tagList.value, oldTabList });
        bus.$emit('afterUpdateTab', { type: 'evict', tab: candidate.tab, tabList: tagList.value, oldTabList });
      });
    }

    function addTabList(target: TabProps, ignoreCache?: boolean) {
      // 忽略 query 等查询参数，以 path 为准，避免 cas 登录后出现 2 个一模一样的 tab 的情况
      // 后期可以考虑加配置来关闭此功能
      const index = tagList.value.findIndex((tag) => equalToRoute(tag.path, undefined, target.path));
      if (index > -1) {
        cacheTabList.delete(tagList.value[index].name);
        // 保留原有的固定状态
        const existingTab = tagList.value[index];
        tagList.value[index] = {
          ...target,
          title: target.title === '' ? existingTab.title : target.title,
          isPinned: existingTab.isPinned,
          pinnedAt: existingTab.pinnedAt,
          lastVisitedAt: Date.now(),
        };
        // 这里有很多次重复的，后续优化好了再上
        // getWujieOrQiankun().then(({ bus }) => {
        //   bus.$emit('afterUpdateTab', { type: 'update', tab: tagList.value[index], tabList: tagList.value, oldTabList });
        // })
      } else {
        // enforceMaxCount 内部可能 splice tagList 并独立 emit 'evict' 事件，
        // 因此 add 的 oldTabList 快照必须在淘汰之后再拍，才能反映 add 前的真实状态。
        enforceMaxCount();
        const oldTabList = tagList.value.slice();
        target.lastVisitedAt = Date.now();
        tagList.value.push(target);
        getWujieOrQiankun().then(({ bus }) => {
          bus.$emit('afterUpdateTab', { type: 'add', tab: target, tabList: tagList.value, oldTabList });
        })
      }

      if (!ignoreCache) {
        cacheTabList.add(target.name as string);
      }

      // 不开启 tabbar 的情况下无法自动更新 title，后期需要解决
      // 更新 title
      if (target.title) {
        setDocumentTitle(target.title)
      }
    }

    function updateTabList(route: RouteLocationNormalized) {
      if (
        route.meta.noAffix ||
        route.meta.isLink
        // || tagList.value.some((tag) => tag.fullPath === route.fullPath) // title 可能发生变化，例如工作台，因此这里不能直接 return
      ) {
        return;
      }

      const target = formatTag(route, settingsStore.settings.route);

      // path 为 homepath 路径时需要更新 title
      if (route.path === DEFAULT_ROUTE.value.path) {
        if (DEFAULT_ROUTE.value.title === DEFAULT_HOME_TITLE) {
          // 如果 title 为默认工作台，则将 title 更新为 path 对应的 title
          homeTitleFromSubApp.value = target.title;
        } else {
          // 否则保持原样（例如 homePath 为效益看板地址，这个时候又不想显示效益看板，想显示为首页）
          target.title = DEFAULT_ROUTE.value.title;
        }
      }

      addTabList(target, !!route.meta.ignoreCache);
    }
    function deleteTag(idx: number) {
      const oldTabList = tagList.value.slice();
      const target = tagList.value.splice(idx, 1)[0];
      cacheTabList.delete(target?.name);
      getWujieOrQiankun().then(({ bus }) => {
        bus.$emit('afterUpdateTab', { type: 'delete', tab: target, tabList: tagList.value, oldTabList });
      })
    }

    /**
     * !用户侧 api，请谨慎修改入参及返回
     * 删除 tag
     * @param path 需要删除的路径
     */
    function deleteTagForUser(path: string) {
      const index = tagList.value.findIndex((tag) => equalToRoute(tag.path, undefined, path));
      if (index < 0) {
        throw new Error(`未找到 ${path} 路径`);
      }

      deleteTag(index);
    }

    /**
     * 清空所有 tag
     */
    function clearTabList() {
      resetTabList();
      router.push(tagList.value[0].fullPath);
    }

    /**
     * !用户侧 api，请谨慎修改入参及返回
     * 清空所有 tag
     */
    function clearTabListForUser() {
      clearTabList()
    }

    function addCache(name: string) {
      if (isString(name) && name !== '') cacheTabList.add(name);
    }
    function deleteCache(tag: TabProps) {
      cacheTabList.delete(tag.name);
    }
    function freshTabList(tags: TabProps[]) {
      const oldTabList = tagList.value.slice();
      tagList.value = tags;
      cacheTabList.clear();
      // 要先判断ignoreCache
      tagList.value
        .filter((el) => !el.ignoreCache)
        .map((el) => el.name)
        .forEach((x) => cacheTabList.add(x));
      getWujieOrQiankun().then(({ bus }) => {
        bus.$emit('afterUpdateTab', { type: 'fresh', tab: null, tabList: tagList.value, oldTabList });
      })
    }
    function resetTabList() {
      const oldTabList = tagList.value.slice();
      tagList.value = [DEFAULT_ROUTE.value];
      cacheTabList.clear();
      cacheTabList.add(DEFAULT_ROUTE.value.name);
      router.push(DEFAULT_ROUTE.value.fullPath);
      getWujieOrQiankun().then(({ bus }) => {
        bus.$emit('afterUpdateTab', { type: 'reset', tab: null, tabList: tagList.value, oldTabList });
      })
    }

    onMounted(async () => {
      const { bus } = await getWujieOrQiankun()
      bus.$on(
        'updateTab',
        ({ type, tag }: { type: '' | 'add' | 'update' | 'delete' | 'reset'; tag?: TabProps } = { type: '' }) => {
          if (type === 'reset') {
            resetTabList();
            return;
          }
          if (!tag) {
            return;
          }

          if (type === 'add' || type === 'update') {
            tagObjByUpdateTab.value[tag.path] = tag
            addTabList(tag);
            return;
          }
          if (type === 'delete') {
            const idx = tagList.value.findIndex((x) => x.fullPath === tag.fullPath);
            const latest = tagList.value[idx - 1]; // 获取队列的前一个tab
            deleteTag(idx);
            if (latest) {
              router.push(latest.fullPath);
            }
            return;
          }
        },
      );
    });

    /**
     * 固定标签到最左侧
     * @param path 标签路径
     */
    function pinTabToLeft(path: string) {
      const currentPinnedCount = tagList.value.filter(tag => tag.isPinned && tag !== tagList.value[0]).length;
      if (currentPinnedCount >= 3) {
        return false; // 已经有3个固定标签，不能再固定
      }

      const index = tagList.value.findIndex((tag) => equalToRoute(tag.path, undefined, path));
      if (index <= 0) return false; // 首页或未找到

      const tab = tagList.value[index];
      if (tab.isPinned) return false; // 已经固定

      // 设置固定状态
      tab.isPinned = true;
      tab.pinnedAt = Date.now();

      // 重新排序
      sortTabs();
      return true;
    }

    /**
     * 取消固定标签
     * @param path 标签路径
     */
    function unpinTab(path: string) {
      const index = tagList.value.findIndex((tag) => equalToRoute(tag.path, undefined, path));
      if (index <= 0) return false; // 首页或未找到

      const tab = tagList.value[index];
      if (!tab.isPinned) return false; // 未固定

      // 取消固定状态
      tab.isPinned = false;
      tab.pinnedAt = undefined;

      // 移除当前标签
      const removedTab = tagList.value.splice(index, 1)[0];
      
      // 将其添加到最右侧
      tagList.value.push(removedTab);

      // 通知子应用
      const oldTabList = tagList.value.slice();
      getWujieOrQiankun().then(({ bus }) => {
        bus.$emit('afterUpdateTab', { type: 'unpin', tab: removedTab, tabList: tagList.value, oldTabList });
      });
      
      return true;
    }

    /**
     * 获取固定标签数量（不包括首页）
     */
    function getPinnedTabCount(): number {
      return tagList.value.filter(tag => tag.isPinned && tag !== tagList.value[0]).length;
    }

    /**
     * 判断是否可以固定更多标签
     */
    function canPinMoreTabs(): boolean {
      return getPinnedTabCount() < 3;
    }

    /**
     * 标签排序：首页 -> 固定标签（按时间排序） -> 普通标签
     */
    function sortTabs() {
      const homeTab = tagList.value[0]; // 首页始终在第一位
      const otherTabs = tagList.value.slice(1);

      // 分离固定和普通标签
      const pinnedTabs = otherTabs.filter(tab => tab.isPinned);
      const normalTabs = otherTabs.filter(tab => !tab.isPinned);

      // 固定标签按固定时间排序
      pinnedTabs.sort((a, b) => (a.pinnedAt || 0) - (b.pinnedAt || 0));

      // 重新组合
      tagList.value = [homeTab, ...pinnedTabs, ...normalTabs];

      // 通知子应用
      const oldTabList = tagList.value.slice();
      getWujieOrQiankun().then(({ bus }) => {
        bus.$emit('afterUpdateTab', { type: 'sort', tab: null, tabList: tagList.value, oldTabList });
      });
    }

    return {
      tagListByShow,
      tagList,
      tagObjByUpdateTab,
      cacheTabList,
      updateTabList,
      deleteTag,
      deleteTagForUser,
      clearTabList,
      clearTabListForUser,
      addCache,
      deleteCache,
      freshTabList,
      resetTabList,
      pinTabToLeft,
      unpinTab,
      getPinnedTabCount,
      canPinMoreTabs,
      sortTabs,
    };
  },
  {
    // @ts-ignore
    get persist() {
      const {
        settings: { system, tabBar },
      } = useSettingsStore();
      let key = getEmpCode();
      if (tabBar.ignoreTabBarPersistent || !key || !system || !tabBar) {
        return false;
      }

      key = `${system.systemCode || `${system.webTitle}-${system.webSubTitle}`}-${key}`;
      const storage = tabBar.cacheType === 'sessionStorage' ? sessionStorage : localStorage;
      return { key, storage };
    },
  },
);

export default useTabBarStore;
